import { Conditional, Text } from '@cashfree-intl/coherent';
import PropTypes from 'prop-types';
import React, { memo, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  generateReCAPTCHA,
  getDeviceId,
  getViewport,
  sentryCaptureException,
} from 'utils/common';
import { ALIAS_LOGIN, EMAIL_LOGIN } from 'utils/constants';

import EVENTS from 'constants/analytics';
import Analytics from 'utils/analytics';
import { trackLoginEvent } from 'containers/Login/helper';
import { trackSignupEvent } from 'containers/Signup/helper';
import { getGeoLocation } from 'hooks/useGeoLocation';
import { AuthContext } from '../../../contexts/AuthContext';

import {
  ACCOUNT_EXISTS,
  ACCOUNT_NOT_FOUND,
  FORGOT_PASSWORD,
  LOGIN,
  RESET_PASSWORD,
  SIGNUP,
  SIGNUP_TYPE,
  USER_DETAILS_COMPLETED,
  loginSignupErrorCode,
} from '../constants';

const GoogleLogin = ({
  enableLoader,
  formName,
  signUpFormStatus,
  loginForm,
  googleLogin,
  navigateAsPerUserFlow,
  resetModalName,
  setAccountExistModalData,
  onboardingActions,
  checkEmailExists,
  disableLoader,
}) => {
  const navigate = useNavigate();
  const [showBtnDivider, resetBtnDivider] = useState(false);
  const [viewPortWidth] = getViewport();
  const { userObj } = useContext(AuthContext);

  const isGoogle =
    window.google && window.google.accounts && window.google.accounts.id;

  const shouldInitializeGoogle =
    isGoogle &&
    formName !== RESET_PASSWORD &&
    formName !== FORGOT_PASSWORD &&
    signUpFormStatus !== USER_DETAILS_COMPLETED;

  const showGoogleOneTap =
    isGoogle &&
    ((formName === LOGIN && loginForm !== ALIAS_LOGIN) ||
      (formName === SIGNUP && signUpFormStatus !== USER_DETAILS_COMPLETED));

  const handleGoogleSignup = async googleToken => {
    /**
     * When user selects Google to signup, we should only verify whether
     * the corresponding email is already in use in our DB.
     *
     * 1. If it exists, show a toast message that says 'Account already exists, login with existing account or use a new Email address' and let user navigate
     *    to login if they want to proceed or else signup with a new email address
     * 2. If not, then save the details locally and take user to Step-2
     *
     * NOTE: User data(both Step-1 and Step-2 data) should only be sent to server after user completes Signup (Step-2)
     * and verifies their phone via OTP
     */

    const submitValidateEmail = async captchaResponse => {
      checkEmailExists({
        googleToken,
        deviceId: await getDeviceId(),
        captchaResponse,
        signupType: SIGNUP_TYPE.GOOGLE,
        navigate,
        onSuccess: response => {
          trackSignupEvent({
            action: 'validate_email',
            status: 'success',
            signupType: SIGNUP_TYPE.GOOGLE,
            email: response?.email,
          });
        },
        onError: error => {
          trackSignupEvent({
            action: 'validate_email',
            status: 'error',
            signupType: SIGNUP_TYPE.GOOGLE,
            message: error?.message,
            email: error?.data?.email,
          });
        },
      });
    };
    generateReCAPTCHA({
      onSuccess: submitValidateEmail,
      onError: () => {
        submitValidateEmail(' ');
        disableLoader();
        sentryCaptureException({
          errMsg: new Error(`${SIGNUP_TYPE.GOOGLE}: Failed to load recaptcha`),
          priority: 'p0',
          label: 'auth',
        });
      },
    });
  };

  const handleGoogleResponse = async response => {
    enableLoader();
    let eventProp = {};
    if (formName === LOGIN || window.location.pathname.indexOf('login') > -1) {
      eventProp = {
        page: 'login',
      };

      const submitGoogleLogin = async (latitude, longitude) => {
        googleLogin({
          body: {
            googleToken: response.credential,
            deviceId: await getDeviceId(),
            latitude,
            longitude,
          },
          navigate,
          actions: { onboardingActions },
          successCb: res => {
            Analytics.trackEvent(
              EVENTS.GOOGLE_LOGIN.LOGIN_SUCCESS,
              { loginType: 'google login' },
              'OB',
            )();
            userObj.login(res);
            if (res.emailVerified === 'false') {
              resetModalName(ACCOUNT_EXISTS);
              setAccountExistModalData(res);
            } else {
              navigateAsPerUserFlow(res.token);
            }
            trackLoginEvent({
              loginType: 'google_login',
              status: 'success',
              latitude,
              longitude,
              locationPermissionGranted: !!latitude,
            });
          },
          errorCb: data => {
            if (data.errorCode === loginSignupErrorCode.USER_NOT_FOUND)
              resetModalName(ACCOUNT_NOT_FOUND);
            else {
              resetModalName(ACCOUNT_EXISTS);
              setAccountExistModalData(data);
            }
            trackLoginEvent({
              loginType: 'google_login',
              status: 'error',
              latitude,
              longitude,
              locationPermissionGranted: !!latitude,
              message: data.errorCode,
            });
          },
        });
      };

      /**
       * in this particular case of GoogleLogin, we must use the callback approach to
       * retrieve and pass the latitude and longitude info to Google Login api.
       *
       * Why not the usual approach, i.e., getting info from AuthProvider?
       * Because on initialization of google script, the callback "handleGoogleResponse" gets
       * wrapped with the data available at the time of initialization. And since the location
       * data takes a bit of time to be retrieved, callback function ignores the further changes
       * in the global scope, i.e., Provider. Which leads the location info to always be null only.
       */
      getGeoLocation(submitGoogleLogin);
    } else {
      eventProp = {
        page: 'signup',
      };

      handleGoogleSignup(response.credential);
    }
    Analytics.trackEvent(
      EVENTS.GOOGLE_LOGIN.GOOGLE_LOGIN_BTN,
      eventProp,
      'OB',
    )();
  };

  useEffect(() => {
    if (shouldInitializeGoogle)
      window.google.accounts.id.initialize({
        client_id: process.env.GOOGLE_SIGNIN_CLIENT_ID,
        callback: handleGoogleResponse,
        cancel_on_tap_outside: false,
        itp_support: true,
        prompt_parent_id: 'googleOneTap',
        context: formName === SIGNUP ? 'signup' : 'signin',
      });
  }, [formName]);

  useEffect(() => {
    // Disabling Google One Tap for Alias Login -> Mobile View
    const oneTapIframeNode = document.getElementById(
      'credential_picker_iframe',
    );
    if (oneTapIframeNode && loginForm === ALIAS_LOGIN) {
      oneTapIframeNode.style.display = 'none';
    }

    if (showGoogleOneTap) {
      window.google.accounts.id.prompt(notification => {
        const btnNode = document.getElementById('googleBtn');

        if (notification.isDismissedMoment()) resetBtnDivider(false);
        if (notification.isDisplayed() && viewPortWidth > 767)
          setTimeout(() => resetBtnDivider(true), 500);
        if (
          (notification.isNotDisplayed() || notification.isSkippedMoment()) &&
          btnNode
        ) {
          resetBtnDivider(true);
          window.google.accounts.id.renderButton(
            document.getElementById('googleBtn'),
            {
              theme: 'outline',
              size: 'large',
              width: 318,
              logo_alignment: 'center',
              text: formName === SIGNUP ? 'signup_with' : 'signin_with',
            },
          );
        }
      });
    } else resetBtnDivider(false);
  }, [formName, loginForm]);

  return (
    <>
      <Conditional if={loginForm === EMAIL_LOGIN}>
        <Conditional if={isGoogle && showBtnDivider}>
          <Text color="placeholder" className="mr-top-8 mr-btm-8 w-600">
            or
          </Text>
        </Conditional>
        {/* Google Signin Button */}
        <div id="googleBtn" />
        {/* Google One Tap */}
        <div id="googleOneTap" />
      </Conditional>
    </>
  );
};

GoogleLogin.propTypes = {
  enableLoader: PropTypes.func.isRequired,
  disableLoader: PropTypes.func.isRequired,
  formName: PropTypes.string.isRequired,
  signUpFormStatus: PropTypes.string.isRequired,
  loginForm: PropTypes.string.isRequired,
  googleLogin: PropTypes.func.isRequired,
  checkEmailExists: PropTypes.func.isRequired,
  navigateAsPerUserFlow: PropTypes.func.isRequired,
  resetModalName: PropTypes.func.isRequired,
  setAccountExistModalData: PropTypes.func.isRequired,
  onboardingActions: PropTypes.object.isRequired,
};

export default memo(GoogleLogin);
